import { z } from 'zod';
import { materialTypeSchema, IMaterialType } from './materialType';

/**
 * Schema for validating material type form data.
 * Extends the base material type schema with form-specific validations.
 *
 * @property {string} name - Material type name (max 255 characters)
 * @property {boolean} isPackType - Whether this material type is used for packing
 */
export const materialTypeFormSchema = materialTypeSchema.extend({
});

/**
 * Default schema for material type form initialization.
 * Extends the form schema with form-specific fields and defaults.
 * Makes nonempty string fields optional with defaults.
 *
 * @property {string} name - Optional material type name
 */
export const materialTypeDefaultFormSchema = materialTypeFormSchema.extend({
    name: z.string().optional().default(''),
});

/**
 * TypeScript interface for material type form values.
 * Generated from the material type form schema for type safety.
 */
export type IMaterialTypeFormValues = z.infer<typeof materialTypeFormSchema>;

/**
 * Converts a Material Type model to form values for editing.
 * If no material type is provided, returns default form values.
 *
 * @param {IMaterialType} [materialType] - Optional material type model to convert
 * @returns {IMaterialTypeFormValues} Form values ready for editing
 */
export const MaterialTypeFormValues = (materialType ?: IMaterialType) : IMaterialTypeFormValues => {
    if (!materialType) return materialTypeDefaultFormSchema.parse({});

    return materialTypeFormSchema.parse({
        ...materialType,
    });
};

/**
 * Validates material type form values against the schema.
 * Logs validation errors to console if validation fails.
 *
 * @param {IMaterialTypeFormValues} formValues - The form values to validate
 * @returns {boolean} True if validation succeeds, false otherwise
 */
export const validateMaterialTypeForm = (formValues : IMaterialTypeFormValues) : boolean => {
    try {
        materialTypeFormSchema.parse(formValues);
        return true;
    } catch (error) {
        if (error instanceof z.ZodError) {
            // eslint-disable-next-line no-console
            console.error('Material type form validation failed:', error.errors);
        } else {
            // eslint-disable-next-line no-console
            console.error('An unexpected error occurred during validation:', error);
        }
        return false;
    }
};
